import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { AppButton } from "@/components/ui";
import { Layers } from "lucide-react";
import { useScrollSpy, getScrollBehavior } from "@/hooks/use-scroll-spy";
import { MobileDrawer } from "./mobile-drawer";

const NAV_LINKS = [
  { key: "howItWorks", href: "#how-it-works", sectionId: "how-it-works" },
  { key: "outcomes", href: "#strategic-outcomes", sectionId: "strategic-outcomes" },
  { key: "platform", href: "#platform", sectionId: "platform" },
  { key: "faq", href: "#faq", sectionId: "faq" },
];

/**
 * Fixed navigation bar for the landing page.
 *
 * Features:
 * - Scroll-aware: adds backdrop blur and border after scrolling past 20px
 * - Active section highlighting via useScrollSpy
 * - Mobile: collapses to hamburger menu → MobileDrawer
 * - CTA button scrolls to waitlist
 * - Rosé Pine Dawn luxury aesthetic
 */
export function LandingNav() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const activeSection = useScrollSpy();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: getScrollBehavior() });
    }
  };

  const handleNavClick = (href: string) => {
    const id = href.replace("#", "");
    scrollToSection(id);
  };

  return (
    <header
      role="banner"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/90 backdrop-blur-md py-3 border-b border-border"
          : "bg-transparent py-6"
      }`}
    >
      <nav
        role="navigation"
        aria-label={t("landing.nav.ariaLabel")}
        className="max-w-7xl mx-auto px-6 flex items-center justify-between"
      >
        {/* Logo */}
        <AppButton
          variant="ghost"
          onPress={() => window.scrollTo({ top: 0, behavior: getScrollBehavior() })}
          className="flex items-center gap-3 h-auto min-w-0 p-0 bg-transparent hover:bg-transparent data-[hovered]:bg-transparent group cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <div className="w-10 h-10 bg-gradient-to-br from-accent to-success rounded-xl flex items-center justify-center shadow-lg group-hover:rotate-6 transition-transform">
            <Layers className="text-background w-6 h-6" />
          </div>
          <span className="font-bold text-xl tracking-tight text-foreground">
            Neural <span className="text-success">AI</span>
          </span>
        </AppButton>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-10 text-sm font-semibold text-foreground-muted">
          {NAV_LINKS.map((link) => (
            <AppButton
              key={link.key}
              variant="ghost"
              onPress={() => handleNavClick(link.href)}
              className={`h-auto min-w-0 p-0 bg-transparent hover:bg-transparent data-[hovered]:bg-transparent text-sm font-semibold text-foreground-muted hover:text-success transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
                activeSection === link.sectionId ? "text-success" : ""
              }`}
            >
              {t(`landing.nav.${link.key}`)}
            </AppButton>
          ))}
          <AppButton
            variant="ghost"
            onPress={() => navigate("/sign-in")}
            className="h-auto min-w-0 p-0 bg-transparent hover:bg-transparent data-[hovered]:bg-transparent text-sm font-semibold text-foreground-muted hover:text-success transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ml-4"
          >
            {t("landing.nav.signIn")}
          </AppButton>
          <AppButton
            onPress={() => scrollToSection("waitlist")}
            className="bg-success text-background px-7 py-2.5 h-auto min-w-0 rounded-full hover:shadow-xl hover:-translate-y-0.5 transition-all shadow-lg shadow-success/20 ml-4"
          >
            {t("landing.nav.cta")}
          </AppButton>
        </div>

        {/* Mobile hamburger */}
        <AppButton
          isIconOnly
          variant="ghost"
          onPress={() => setIsDrawerOpen(true)}
          className="rounded-lg p-2 h-auto min-w-0 bg-transparent hover:bg-transparent data-[hovered]:bg-transparent text-foreground-muted hover:text-foreground md:hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent transition-colors"
          aria-expanded={isDrawerOpen}
          aria-controls="mobile-drawer"
          aria-label="Open menu"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </AppButton>
      </nav>

      <MobileDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </header>
  );
}
